import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { ThemeProvider } from "@/components/shell/theme-provider";
import { ApiProvider } from "@/api/abstractions/api-provider";
import { UserProvider } from "@/context/user-context";
import { useMsalInit } from "@/hooks/use-msal-init";
import { PWAOnlineStatus, PWAUpdateNotification, PWACacheResolver } from "@/components/pwa";
import { PWAInstallPrompt } from "@/components/pwa";
import { PwaInstallProvider } from "@/pwa/pwa-install-provider";
import {
  Login,
  Logout,
  MsalErrorState,
  MsalLoadingState,
  Unauthorized
} from "@/components/security";
import { ProtectedRoute } from "@/components/security";
import { UserRegistrationHandler } from "@/components/registration";
import { AppShell } from "@/components/shell";
import { getMsalInstance } from "@/config/auth-config";
import { useMsalEvents } from "./hooks/use-msal-events";
import { LoadingCard } from "./components/ui/loading-card";
import { LookupsProvider } from "./context/use-lookups";

// Lazy load dashboard components
const AdminDashboard = lazy(() => import("@/modules/admin/admin-dashboard"));
const PracticeDashboard = lazy(
  () => import("@/modules/practices/practice-dashboard")
);
const ProfessionalDashboard = lazy(
  () => import("@/modules/professionals/professional-dashboard")
);
// MSAL Debug component (development only)
const MsalDebug = lazy(() =>
  import("@/components/security/msal-debug").then((m) => ({
    default: m.MsalDebug
  }))
);
const PWADebug = lazy(() =>
  import("@/components/pwa/pwa-debug").then((m) => ({ default: m.PWADebug }))
);

// Component to handle MSAL events when Redux store is available
function MsalEventHandler() {
  useMsalEvents();
  return null;
}

// Loading component for lazy-loaded modules
const DashboardLoading = () => <LoadingCard spinnerSize="lg" />;

function App() {
  // MSAL Initialization
  const { isInitialized, error, isLoading } = useMsalInit();

  // Show loading state while MSAL is Loading or Initializing
  if (isLoading) {
    return <MsalLoadingState />;
  }

  // Show error state if MSAL initialization failed
  if (error) {
    return <MsalErrorState error={error} />;
  }

  // Only render the app when MSAL is properly initialized
  if (!isInitialized) {
    return null;
  }

  // Get MSAL instance after initialization is complete
  const msalInstance = getMsalInstance();

  return (
    <MsalProvider instance={msalInstance}>
      <ApiProvider>
        <MsalEventHandler />
        <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
          <PwaInstallProvider>
            <UserProvider>
              <LookupsProvider>
                <PWAUpdateNotification />
                <PWAOnlineStatus />
                <PWACacheResolver />
                <PWAInstallPrompt />
                <Router>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/logout" element={<Logout />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Debug routes - only available in development */}
                    {import.meta.env.DEV && (
                      <>
                        <Route path="/debug/msal" element={<MsalDebug />} />
                        <Route path="/debug/pwa" element={<PWADebug />} />
                      </>
                    )}

                    {/* Protected routes with unified shell layout */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <UserRegistrationHandler>
                            <AppShell />
                          </UserRegistrationHandler>
                        </ProtectedRoute>
                      }
                    >
                      {/* Root route - handled by AppShell for role-based redirect */}
                      <Route index element={<div />} />

                      {/* Admin module routes — support shares this console; the
                          sensitive actions inside it are gated per-action. */}
                      <Route
                        path="admin/*"
                        element={
                          <ProtectedRoute requiredUserTypes={["admin", "support"]}>
                            <Suspense fallback={<DashboardLoading />}>
                              <AdminDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      {/* Practice module routes */}
                      <Route
                        path="practice/*"
                        element={
                          <ProtectedRoute requiredUserTypes={["practice"]}>
                            <Suspense fallback={<DashboardLoading />}>
                              <PracticeDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      {/* Professional module routes */}
                      <Route
                        path="professional/*"
                        element={
                          <ProtectedRoute requiredUserTypes={["professional"]}>
                            <Suspense fallback={<DashboardLoading />}>
                              <ProfessionalDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      {/* Catch all route - redirect to root for role-based handling */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>

                    {/* Catch all route - redirect to login */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </Router>
              </LookupsProvider>
            </UserProvider>
          </PwaInstallProvider>
        </ThemeProvider>
      </ApiProvider>
    </MsalProvider>
  );
}

export default App;
